import type { LogLevel, ResolvedCaptureConsole } from '../types.js';

export type ConsoleMethod = (...args: unknown[]) => void;

export type ConsoleTarget = {
  error?: ConsoleMethod;
  warn?: ConsoleMethod;
  info?: ConsoleMethod;
  log?: ConsoleMethod;
  debug?: ConsoleMethod;
} & Record<string, unknown>;

const LEVEL_TO_METHOD: Record<LogLevel, keyof ConsoleTarget> = {
  error: 'error',
  warn: 'warn',
  info: 'info',
  log: 'log',
  debug: 'debug',
};

export interface PatchConsoleHandle {
  unpatch: () => void;
}

export function patchConsole(
  target: ConsoleTarget,
  levels: ResolvedCaptureConsole,
  onCapture: (level: LogLevel, args: unknown[]) => void,
  runGuarded: <T>(fn: () => T) => T,
  isGuardActive: () => boolean,
): PatchConsoleHandle {
  const originals = new Map<LogLevel, ConsoleMethod>();
  const levelEntries = Object.entries(levels) as [LogLevel, boolean][];

  for (const [level, on] of levelEntries) {
    if (!on) continue;
    const methodName = LEVEL_TO_METHOD[level];
    const original = target[methodName] as ConsoleMethod | undefined;
    if (typeof original !== 'function') continue;
    originals.set(level, original);

    const patched: ConsoleMethod = (...args: unknown[]) => {
      try {
        original.apply(target, args);
      } catch {
        /* swallow original failures so SDK doesn't break host */
      }
      if (isGuardActive()) return;
      try {
        runGuarded(() => onCapture(level, args));
      } catch {
        /* swallow so host code never sees SDK throw from console */
      }
    };
    (target as Record<string, unknown>)[methodName] = patched;
  }

  return {
    unpatch: () => {
      for (const [level, original] of originals) {
        const methodName = LEVEL_TO_METHOD[level];
        (target as Record<string, unknown>)[methodName] = original;
      }
      originals.clear();
    },
  };
}
